import React, { useCallback, useState } from "react";
import * as Styled from "./styles";
import DealerResaleInfo from "./components/DealerResaleInfo";
import DealerWhatsAppConfig from "./components/DealerWhatsAppConfig";
import DealerPaymentConfig from "./components/DealerPaymentConfig";
import DealerAcquirersTable from "./components/DealerAcquirersTable";
import DealerProfile from "./components/DealerProfile";
import DealerServices from "./components/DealerServices";
import { useDealerData } from "./hooks/useDealerData";
import { mergeDealerDataWithMcc, ProcessedProvider } from "./helpers";
import { convertProcessedToAvailable } from "./utils/providerConverter";
import type { Provider } from "./components/DealerAcquirersTable/types";
import ProviderAdditionalStepModal from "./components/DealerInfo/components/OnlinePaymentsConfig/Components/ProviderAdditionalStepModal";
import {
  hasProviderAdditionalStep,
  normalizeProviderAdditionalStep,
} from "./components/DealerInfo/components/OnlinePaymentsConfig/utils/additionalStepContent";
import type { ProviderAdditionalStep } from "services/paymentApi.types";
// import DealerPaymentDeliveryOld from "./components/DealerPaymentDeliveryOld";
// import type { DealerSearchByIdResponse } from "store/modules/DealerCare/types.store";
import DealerPaymentDelivery from "./components/DealerPaymentDelivery";

const DealerById = () => {
  const {
    dealerData,
    providers,
    availableProviders,
    merchantInfo,
    localPaymentData,
    updateMcc,
    removeProvider,
    refreshProviders,
    dealerId,
    updateProviderState,
    updateProvider,
    paymentMethods,
  } = useDealerData();

  const [additionalStepAfterAdd, setAdditionalStepAfterAdd] =
    useState<ProviderAdditionalStep | null>(null);

  const handleProviderDeleted = useCallback(
    (providerId?: string) => {
      if (providerId) {
        removeProvider(providerId);
      }
    },
    [removeProvider],
  );

  const handleProviderAdded = useCallback(
    async (provider?: Provider) => {
      let refreshedProviders: Provider[] | null = null;
      if (dealerId) {
        refreshedProviders = await refreshProviders(dealerId);
      }

      const providerFromList =
        provider?.id && refreshedProviders
          ? refreshedProviders.find((item) => item.id === provider.id)
          : undefined;
      const step =
        providerFromList?.additionalSteps ?? provider?.additionalSteps ?? null;

      if (hasProviderAdditionalStep(step)) {
        setAdditionalStepAfterAdd(normalizeProviderAdditionalStep(step));
      }
    },
    [dealerId, refreshProviders],
  );

  const handleMccUpdate = useCallback(
    (categoryCode: string) => {
      updateMcc(categoryCode);
    },
    [updateMcc],
  );

  const handleProviderStatusChanged = useCallback(
    (providerId?: string, newState?: string) => {
      if (providerId && newState) {
        updateProviderState(providerId, newState);
      }
    },
    [updateProviderState],
  );

  const handleProviderUpdated = useCallback(
    (provider?: Provider) => {
      if (provider) {
        updateProvider(provider);
      }
    },
    [updateProvider],
  );

  // const dealerSearchData =
  //   dealerData && paymentMethods
  //     ? ({
  //         dealerInfo: dealerData,
  //         paymentMethods,
  //       } as DealerSearchByIdResponse)
  //     : null;

  const dealerDataWithMcc = mergeDealerDataWithMcc(dealerData, merchantInfo);

  const hasProviders = providers && providers.length > 0;
  // const hasOnlinePaymentEnabled =
  //   dealerDataWithMcc?.attributes?.onlinePaymentEnabled;

  return (
    <Styled.Container>
      <Styled.Content>
        <DealerResaleInfo dealerData={dealerDataWithMcc} />

        <DealerServices dealerData={dealerData} />

        <DealerWhatsAppConfig dealerData={dealerData} />
        {/* {hasOnlinePaymentEnabled ? ( */}
        <DealerPaymentDelivery
          localPaymentData={localPaymentData}
          dealerId={dealerData?.id || ""}
          paymentMethodsFromApi={paymentMethods ?? null}
          availableProviders={availableProviders as ProcessedProvider[]}
        />
        {/* ) : (
          dealerSearchData && (
            <DealerPaymentDeliveryOld
              data={{
                dealerData: dealerSearchData,
                dealerTradeName: dealerSearchData.dealerInfo.tradeName,
                dealerEnabled: dealerSearchData.dealerInfo.enabled,
                dealerId: dealerSearchData.dealerInfo.id,
              }}
            />
          )
        )} */}

        <DealerPaymentConfig
          dealerData={dealerDataWithMcc}
          onMccUpdate={handleMccUpdate}
          onProviderAdded={handleProviderAdded}
          merchantInfo={merchantInfo}
          availableProviders={availableProviders}
          dealerProviders={providers}
          hasProviders={hasProviders}
        />
        <DealerAcquirersTable
          providers={providers}
          onProviderDeleted={handleProviderDeleted}
          onProviderStatusChanged={handleProviderStatusChanged}
          onProviderAdded={handleProviderAdded}
          onProviderUpdated={handleProviderUpdated}
          availableProviders={
            availableProviders
              ? convertProcessedToAvailable(availableProviders)
              : undefined
          }
          merchantInfo={merchantInfo}
        />
        {dealerData && <DealerProfile data={dealerData} />}
      </Styled.Content>

      {additionalStepAfterAdd && (
        <ProviderAdditionalStepModal
          open={Boolean(additionalStepAfterAdd)}
          additionalStep={additionalStepAfterAdd}
          onClose={() => setAdditionalStepAfterAdd(null)}
        />
      )}
    </Styled.Container>
  );
};

export default DealerById;
